import type { Namespace, TFunction } from 'i18next';
import {
  AIRPORTS,
  airportLabel,
  countryIso2,
  countryLabel,
  expandScope,
  expandScopes,
  regionLabel,
} from '@nexa/airport-reference';
import type { ScopeEntry } from '@nexa/auth';

// Work-scope helpers for the access screens (users-roles-permissions §4.18): a scope is a
// LIST of `{kind: REGION|COUNTRY|AIRPORT, code}` entries or the single `{kind: ALL}`.
// Friendly labels primary, codes secondary (frontend-conventions §18.3): country names are
// localized with `Intl.DisplayNames`; airports read as their IATA codes in chips
// ("GRU · SCL") with the city names as the hover reference.

/** Stable string id of an entry — `ALL`, `REGION:BR`, `COUNTRY:BRA`, `AIRPORT:GRU`. */
export const ALL_SCOPE_KEY = 'ALL';
export const WHOLE_AIRLINE: readonly ScopeEntry[] = Object.freeze([{ kind: 'ALL' }]);

export function scopeEntryKey(entry: ScopeEntry): string {
  return entry.kind === 'ALL' ? ALL_SCOPE_KEY : `${entry.kind}:${entry.code}`;
}

const CODED = new Set(['REGION', 'COUNTRY', 'AIRPORT']);

export function scopeEntryOf(key: string): ScopeEntry | null {
  if (key === ALL_SCOPE_KEY) return { kind: 'ALL' };
  const at = key.indexOf(':');
  const kind = key.slice(0, at);
  const code = key.slice(at + 1);
  if (at < 0 || !CODED.has(kind) || !code) return null;
  return { kind: kind as 'REGION' | 'COUNTRY' | 'AIRPORT', code };
}

/** The scope is the airline's whole operating set. */
export function isWholeAirline(scopes: readonly ScopeEntry[]): boolean {
  return scopes.some((e) => e.kind === 'ALL');
}

/** Does the scope reach this airport? (`ALL` reaches every one.) */
export function scopeCoversAirport(scopes: readonly ScopeEntry[], iata: string): boolean {
  const airports = expandScopes(scopes);
  return airports === '*' || airports.includes(iata);
}

/**
 * Can an actor scoped to `actorScope` GRANT `entry`? Mirrors the BFF guardrail
 * (`scopeEscalation`: both sides expanded over the airport catalog, so a scope always
 * covers itself): every airport the entry covers must be inside the actor's own scope; an
 * `ALL` entry needs an `ALL` actor. An entry that covers nothing is refused (fail closed).
 */
export function entryWithin(actorScope: readonly ScopeEntry[], entry: ScopeEntry): boolean {
  const mine = expandScopes(actorScope);
  if (mine === '*') return true;
  if (entry.kind === 'ALL') return false;
  const theirs = expandScope(entry);
  if (theirs.length === 0) return false;
  const covered = new Set(mine);
  return theirs.every((iata) => covered.has(iata));
}

// ── localized place names ─────────────────────────────────────────────────────

const displayNames = new Map<string, Intl.DisplayNames | null>();

function regionNamesFor(lng: string): Intl.DisplayNames | null {
  if (!displayNames.has(lng)) {
    try {
      displayNames.set(lng, new Intl.DisplayNames([lng], { type: 'region' }));
    } catch {
      displayNames.set(lng, null);
    }
  }
  return displayNames.get(lng) ?? null;
}

function iso2OfCountry(code: string): string | undefined {
  const any = Object.values(AIRPORTS).find((a) => a.country === code);
  return any ? countryIso2(any.iata) : undefined;
}

/** A country (ISO alpha-3) in the operator's language — "Brasil" / "Brazil". */
export function countryName(code: string, lng: string): string {
  const iso2 = iso2OfCountry(code);
  const localized = iso2 ? regionNamesFor(lng)?.of(iso2) : undefined;
  return localized && localized !== iso2 ? localized : countryLabel(code);
}

/**
 * A scope region. A region that is exactly one country (the LATAM codes `BR`, `CL`, …) is
 * named like that country; a multi-country region keeps the reference label.
 */
export function regionName(code: string, lng: string): string {
  const countries = new Set(
    Object.values(AIRPORTS)
      .filter((a) => a.region === code)
      .map((a) => a.country),
  );
  if (countries.size === 1) {
    const [only] = [...countries];
    if (only && iso2OfCountry(only) === code) return countryName(only, lng);
  }
  return regionLabel(code);
}

// ── chips ─────────────────────────────────────────────────────────────────────

export interface IScopeChip {
  id: string;
  label: string;
  /** Hover reference (airport city names). */
  title?: string;
  tone: 'all' | 'place' | 'airports' | 'none';
}

/**
 * The scope as chips: "Toda la aerolínea" · one per region / country · ONE chip for the
 * airports ("GRU · SCL", "+N" past `maxAirports`). No entries → a single "Sin alcance".
 */
export function scopeChips<N extends Namespace>(
  t: TFunction<N>,
  scopes: readonly ScopeEntry[],
  lng: string,
  maxAirports = 4,
): IScopeChip[] {
  if (scopes.length === 0) return [{ id: 'none', label: t('admin:scope.none'), tone: 'none' }];
  if (isWholeAirline(scopes)) return [{ id: ALL_SCOPE_KEY, label: t('admin:scope.all'), tone: 'all' }];
  const chips: IScopeChip[] = [];
  const airports: string[] = [];
  for (const entry of scopes) {
    if (entry.kind === 'ALL') continue;
    if (entry.kind === 'AIRPORT') {
      airports.push(entry.code);
      continue;
    }
    chips.push({
      id: scopeEntryKey(entry),
      label: entry.kind === 'REGION' ? regionName(entry.code, lng) : countryName(entry.code, lng),
      tone: 'place',
    });
  }
  if (airports.length > 0) {
    const shown = airports.slice(0, maxAirports).join(' · ');
    const rest = airports.length - maxAirports;
    chips.push({
      id: 'airports',
      label: rest > 0 ? t('admin:scope.airportsMore', { list: shown, rest }) : shown,
      title: airports.map(airportLabel).join(', '),
      tone: 'airports',
    });
  }
  return chips;
}

/** One line for exports: every entry named in full (no "+N" truncation). */
export function scopeText<N extends Namespace>(
  t: TFunction<N>,
  scopes: readonly ScopeEntry[],
  lng: string,
): string {
  return scopeChips(t, scopes, lng, Number.POSITIVE_INFINITY)
    .map((c) => c.label)
    .join(', ');
}

/** Same entries, any order. */
export function scopeListEquals(a: readonly ScopeEntry[], b: readonly ScopeEntry[]): boolean {
  const ka = new Set(a.map(scopeEntryKey));
  const kb = new Set(b.map(scopeEntryKey));
  return ka.size === kb.size && [...ka].every((k) => kb.has(k));
}
